import { ScanService } from '../services/ScanService';
import { AStoreWithMappedData } from './AStoreWithMappedData';
import { Scan } from '../models/Scan';
import * as assert from 'assert';
import { computed, observable } from 'mobx';

const NUMBER_OF_SCANS_TO_SHOW: number = 6;

export class ScanStore extends AStoreWithMappedData<Scan> {
    @observable private activeScanId?: number;
    private _service: ScanService;

    constructor(authToken: string) {
        super();
        this._service = new ScanService(authToken);
    }

    protected loadData(): Promise<void> {
        return this._service.getAll()
            .then((records: Scan[]) => {
                this.addRecordsToMap(records);
            });
    }

    @computed public get latestScanInDisplayRange(): Scan {
        assert(this.recentScans.length > 0, 'ScanStore#latestScanInDisplayRange - no scans available');
        return this.recentScans[0];
    }

    @computed public get mostRecentScan(): Scan {
        assert(this.sortedByDateDescending.length > 0, 'ScanStore#mostRecentScan - no scans available');
        return this.sortedByDateDescending[0];
    }

    @computed public get sortedByDateDescending(): Scan[] {
        return this.allRecords().sort((a: Scan, b: Scan) => {
            if (a.created_at.valueOf() > b.created_at.valueOf()) {
                return -1;
            } else if (a.created_at.valueOf() < b.created_at.valueOf()) {
                return 1;
            }
            return 0;
        });
    }

    @computed public get sortedByDateAscending(): Scan[] {
        return this.allRecords().sort((a: Scan, b: Scan) => {
            if (a.created_at.valueOf() > b.created_at.valueOf()) {
                return 1;
            } else if (a.created_at.valueOf() < b.created_at.valueOf()) {
                return -1;
            }
            return 0;
        });
    }

    @computed public get recentScans(): Scan[] {
        let out: Scan[] = [];
        const length: number = this.sortedByDateAscending.length;

        if (undefined != this.activeScanId) {
            const index: number = this.sortedByDateAscending.findIndex((scan) => {
                return scan.id === this.activeScanId;
            });
            assert(-1 != index, 'ScanStore#recentScans - failed to find matching record for active scan');

            if (index + 1 > NUMBER_OF_SCANS_TO_SHOW) {
                const start: number = index + 1 - NUMBER_OF_SCANS_TO_SHOW;
                out = this.sortedByDateAscending.slice(start, index + 1);
            } else {
                out = this.sortedByDateAscending.slice(0, index + 1);
            }
        } else {
            if (length < NUMBER_OF_SCANS_TO_SHOW) {
                out = this.sortedByDateAscending;
            } else {
                out = this.sortedByDateAscending.slice(length - NUMBER_OF_SCANS_TO_SHOW);
            }
        }

        assert(out.length <= NUMBER_OF_SCANS_TO_SHOW, 'ScanStore#recentScans - produced too many scans');
        return out;
    }

    public requestHelp(scan_id: number, topic: string): Promise<void> {
        return this._service.requestHelp(scan_id, topic);
    }

    public setActiveScan(scan_id: number): void {
        assert(this.recordsMap.has(scan_id), 'ScanStore#setActiveScan - passed unknown scan ID: ' + scan_id);
        this.activeScanId = scan_id;
    }

    public clearActiveScan(): void {
        this.activeScanId = undefined;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/data_layer/stores/ScanStore.ts